import React, { useEffect, useState } from "react";
import axios from "../utils/axiosConfig";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Container, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, 
  TableRow, Button, Select, MenuItem, Alert 
} from "@mui/material";
import { useAuth } from "../context/AuthContext";
import { useValidation } from '../context/ValidationContext';

import { handleApiError } from "../utils/apiErrorHandler";

import { useRegistrationValidation } from '../hooks/useRegistrationValidation';
import { useRegistrationStatus } from '../hooks/useRegistrationStatus';

interface Official {
  _id: string;
  firstname: string;
  surname: string;
}

interface OfficialRegistration {
  _id: string;
  official: Official;
  type: "Elite" | "Basis";
  role: string;
}

const rolesList = ["Delegationsleitung", "Trainer", "Helfer", "Physio", "Arzt"];

const OfficialRegistrationPage: React.FC = () => {
const { competitionId } = useParams();
const navigate = useNavigate();
const clubId = useAuth();
//const { refreshValidation } = useValidation();
const { refreshValidation } = useRegistrationValidation();

const [registeredOfficials, setRegisteredOfficials] = useState<OfficialRegistration[]>([]);
const [availableOfficials, setAvailableOfficials] = useState<Official[]>([]);
const [loading, setLoading] = useState(true);
const [officialRules, setOfficialRules] = useState<{ type: string; maxOfficialsAllowed: number }[]>([]);

const [onlyBasisAvailable, setOnlyBasisAvailable] = useState<boolean>(false);

const registrationStatus = useRegistrationStatus(competitionId);

useEffect(() => {
  const fetchData = async () => {
    try {
      const regResponse = await axios.get(`/api/official-registrations/${competitionId}`);
      setRegisteredOfficials(regResponse.data);

      const officialResponse = await axios.get(`/api/officials`);
      setAvailableOfficials(officialResponse.data);

      const rulesResponse = await axios.get(`/api/competitions/${competitionId}/official-rules`);
      setOfficialRules(rulesResponse.data);

      const competitionResponse = await axios.get(`/api/competitions/${competitionId}`);
      setOnlyBasisAvailable(competitionResponse.data.onlyBasisRegistrationAvailable || false);

      if (competitionResponse.data.enableOfficial === false) {
        navigate(`/competition-registration/${competitionId}/athletes`);
        return;
      }

      refreshValidation();
      setLoading(false);
    } catch (error) {
      handleApiError(error, "Fehler beim Laden der Offiziellen-Daten.");
    }
  };
  fetchData();
}, [competitionId, clubId]);

const registerOfficial = async (officialId: string, type: "Elite" | "Basis") => {
  try {
    const response = await axios.post(`/api/official-registrations`, {
      competition: competitionId,
      club: clubId,
      official: officialId,
      type,
      role: "Trainer", // Standardwert setzen
    });

    const fullOfficial = availableOfficials.find(o => o._id === officialId) || response.data.official;
    setRegisteredOfficials([...registeredOfficials, { ...response.data, official: fullOfficial }]);
    refreshValidation();
  } catch (error: any) {
    handleApiError(error, "Fehler beim Anmelden des Offiziellen.");
  }
};

const updateRole = async (registrationId: string, newRole: string) => {
  try {
    await axios.patch(`/api/official-registrations/${registrationId}`, { role: newRole });

    setRegisteredOfficials(
      registeredOfficials.map((reg) =>
        reg._id === registrationId ? { ...reg, role: newRole } : reg
      )
    );
    refreshValidation();
  } catch (error: any) {
    handleApiError(error, "Fehler beim Aktualisieren der Rolle.");
  }
};

const unregisterOfficial = async (registrationId: string) => {
  try {
    await axios.delete(`/api/official-registrations/${registrationId}`);
    setRegisteredOfficials(registeredOfficials.filter(o => o._id !== registrationId));
    refreshValidation();
  } catch (error: any) {
    handleApiError(error, "Fehler beim Abmelden des Offiziellen.");
  }
};

return (
  <Container component="main" maxWidth="md">
    <Paper elevation={3} sx={{ padding: 3 }}>
      <Typography variant="h5" gutterBottom>
        Offizielle-Meldung
      </Typography>

      {/* 🔹 Offizielle-Tabellen für Elite & Basis */}
      {(["Elite", "Basis"] as const).map((type) => {
        const maxOfficials = officialRules.find((r) => r.type === type)?.maxOfficialsAllowed || Infinity;
        const currentCount = registeredOfficials.filter((reg) => reg.type === type).length;
        const isLimitReached = currentCount >= maxOfficials;

        return (
          <div key={type}>
            <Typography variant="h6" sx={{ mt: 2 }}>
              {type} Offizielle ({currentCount} / {maxOfficials})
            </Typography>

            {isLimitReached && <Alert severity="warning">Maximale Anzahl erreicht!</Alert>}

            <TableContainer component={Paper} sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Rolle</TableCell>
                    <TableCell>Aktion</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {registeredOfficials
                    .filter((reg) => reg.type === type)
                    .map((reg) => (
                      <TableRow key={reg._id}>
                        <TableCell>{reg.official.firstname} {reg.official.surname}</TableCell>
                        <TableCell>
                          <Select
                            value={reg.role}
                            onChange={(e) => updateRole(reg._id, e.target.value)}
                            size="small"
                            disabled={registrationStatus.isClosed}
                          >
                            {rolesList.map((role) => (
                              <MenuItem key={role} value={role}>
                                {role}
                              </MenuItem>
                            ))}
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Button variant="contained" color="error" size="small" onClick={() => unregisterOfficial(reg._id)} disabled={registrationStatus.isClosed}>
                            Abmelden
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        );
      })}

      {/* 🔹 Verfügbare Offizielle */}
      <Typography variant="h6" sx={{ mt: 3 }}>
        Verfügbare Offizielle
      </Typography>
      <TableContainer component={Paper} sx={{ mt: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Aktion</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {availableOfficials.map((official) => {
              const isRegisteredForElite = registeredOfficials.some((reg) => reg.official._id === official._id && reg.type === "Elite");
              const isRegisteredForBasis = registeredOfficials.some((reg) => reg.official._id === official._id && reg.type === "Basis");

              const maxElite = officialRules.find((r) => r.type === "Elite")?.maxOfficialsAllowed || Infinity;
              const maxBasis = officialRules.find((r) => r.type === "Basis")?.maxOfficialsAllowed || Infinity;

              const currentEliteCount = registeredOfficials.filter((reg) => reg.type === "Elite").length;
              const currentBasisCount = registeredOfficials.filter((reg) => reg.type === "Basis").length;

              const eliteDisabled = isRegisteredForElite || currentEliteCount >= maxElite;
              const basisDisabled = isRegisteredForBasis || currentBasisCount >= maxBasis;

              return (
                <TableRow key={official._id}>
                  <TableCell>{official.firstname} {official.surname}</TableCell>
                  <TableCell>
                    {onlyBasisAvailable ? (
                      // 🆕 Nur ein Button wenn onlyBasisAvailable true ist
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => registerOfficial(official._id, "Basis")}
                        disabled={
                          registeredOfficials.some((reg) => reg.official._id === official._id && reg.type === "Basis") ||
                          registeredOfficials.filter((reg) => reg.type === "Basis").length >= 
                          (officialRules.find((r) => r.type === "Basis")?.maxOfficialsAllowed || Infinity) ||
                          registrationStatus.isClosed
                        }
                      >
                        Anmelden
                      </Button>
                    ) : (
                      // 🔹 Normale zwei Buttons wenn onlyBasisAvailable false/undefined ist
                      <>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => registerOfficial(official._id, "Basis")}
                          disabled={basisDisabled || registrationStatus.isClosed}
                        >
                          Basis anmelden
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          sx={{ ml: 1 }}
                          onClick={() => registerOfficial(official._id, "Elite")}
                          disabled={eliteDisabled || registrationStatus.isClosed}
                        >
                          Elite anmelden
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  </Container>
);
}

export default OfficialRegistrationPage;
